import { Component, OnInit, AfterViewInit, TemplateRef, ViewChild, Input, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { fadeInOut } from '../../../services/animations';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from "../../../services/app-translation.service";
import { Utilities } from "../../../services/utilities";
import { AccountService } from "../../../services/account.service";
import { Configuration, RequirementService, RequirementViewModel } from '../../index';
import { Observable } from 'rxjs';
import { Permission } from '../../../models/permission.model';
import { AuthService } from '../../../services/auth.service';

@Component({
    selector: 'app-requirement-list',
    templateUrl: './requirement-list.component.html',
    styleUrls: ['./requirement-list.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RequirementListComponent implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator, { static: true })
    paginator: MatPaginator;
    @ViewChild(MatSort, { static: true })
    sort: MatSort;

    displayedColumns = ['name', 'businessCategoryName', 'description', 'createdDate', 'updatedDate', 'actions'];
    dataSource: MatTableDataSource<RequirementViewModel>;
    loadingIndicator: boolean;
    currentSearchText: string;


    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private requirementService: RequirementService,
        private snackBar: MatSnackBar) {

        this.dataSource = new MatTableDataSource<RequirementViewModel>();
        this.currentSearchText = "";
    }

    ngOnInit() {
        this.loadData();
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue;
    }

    private refresh() {
        // Causes the filter to refresh there by updating with recently added data.
        this.applyFilter(this.dataSource.filter);
    }

    public loadData() {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;

        this.requirementService.getRequirements().subscribe(
            orgs => this.onDataLoadSuccessful(orgs),
            error => this.onDataLoadFailed(error)
        );
    }

    private onDataLoadSuccessful(f: RequirementViewModel[]) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.dataSource.data = f;
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"), this.translationService.getTranslation("FetchErrorMessage") + "\r\n" + Utilities.getHttpResponseMessage(error),
            MessageSeverity.error, error);
    }


    public confirmDelete(f: RequirementViewModel) {
        let message: string = this.translationService.getTranslation("commands.Delete") +
            " " +
            f.name +
            "?";
        let action: string = this.translationService.getTranslation("commands.Delete").toUpperCase();

        this.snackBar.open(message, action, { duration: 5000 })
            .onAction().subscribe(() => {
                this.alertService.startLoadingMessage(
                    this.translationService.getTranslation("globalTexts.DeletingMessage"));
                this.loadingIndicator = true;

                this.requirementService.deleteRequirement(f.requirementId)
                    .subscribe(results => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;
                        this.alertService.showMessage(this.translationService.getTranslation("commands.Delete"),
                            f.name + " " + this.translationService.getTranslation("globalTexts.deleted"),
                            MessageSeverity.success);
                        this.loadData();
                    },
                    error => {
                        this.alertService.stopLoadingMessage();
                        this.loadingIndicator = false;

                        let errorMessage = this.translationService.getTranslation("globalTexts.Error") +
                            ": " +
                            Utilities.getHttpResponseMessage(error);

                        this.alertService.showStickyMessage(
                            this.translationService.getTranslation("globalTexts.DeleteError"),
                            errorMessage,
                            MessageSeverity.error,
                            error);
                    });
            });
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

    hasPermissionToDelete(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }
}
